import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal, Trash2, Users, WalletCards } from 'lucide-react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Empty } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { WorkflowStatusSelect } from '@/components/jobs/workflow-status-select'
import { AssignmentDeclineChainIndicator } from '@/components/assignments/assignment-decline-chain'
import {
  formatAssignmentPayoutDisplay,
  getActualAssignmentPayoutDisplay,
  getEstimatedAssignmentPayoutDisplay,
} from '@/lib/assignment-pay'
import { getErrorMessage } from '@/lib/api'
import {
  canDeleteAssignment,
  getAssignmentDeleteBlockedMessage,
  getAssignmentStatusBadgeClass,
  getAssignmentStatusLabel,
  isTerminalAssignmentStatus,
} from '@/lib/assignment-status'
import { formatCurrency } from '@/lib/utils'
import { withReturnTo } from '@/lib/return-navigation'
import { withServiceInstanceId } from '@/lib/service-instance'
import { assignmentsApi } from '@/services/assignments'
import jobWorkflowApi from '@/services/job-workflow'
import type { Assignment, JobWorkflowTransition, WorkDayEntry } from '@/types'

type JobAssignmentsTableProps = {
  jobId: string | number
  serviceInstanceId?: string | null
  currentReturnPath: string
  assignments: Assignment[]
  isLoading: boolean
  jobModificationBlocked: boolean
  jobStatusChangeDisabled: boolean
  estimatedWorkerPayoutTotal: number
  actualPayoutTotal: number
  assignmentTableDate?: string
  workDayEntries: WorkDayEntry[]
  formatAssignmentPayscale: (assignment: Assignment) => { label: string; amountLabel?: string | null }
  onNavigate: (path: string) => void
  onOpenWorkdayEntries: (assignment: Assignment) => void
}

export function JobAssignmentsTable({
  jobId,
  serviceInstanceId,
  currentReturnPath,
  assignments,
  isLoading,
  jobModificationBlocked,
  jobStatusChangeDisabled,
  estimatedWorkerPayoutTotal,
  actualPayoutTotal,
  assignmentTableDate,
  workDayEntries,
  formatAssignmentPayscale,
  onNavigate,
  onOpenWorkdayEntries,
}: JobAssignmentsTableProps) {
  const queryClient = useQueryClient()
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<string[]>([])
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false)

  const selectableAssignmentIds = useMemo(
    () => (jobModificationBlocked ? [] : assignments.map((assignment) => String(assignment.id))),
    [assignments, jobModificationBlocked],
  )
  const deletableAssignments = useMemo(
    () => assignments.filter((assignment) => canDeleteAssignment(assignment) && !jobModificationBlocked),
    [assignments, jobModificationBlocked],
  )
  const deletableAssignmentIds = useMemo(
    () => deletableAssignments.map((assignment) => String(assignment.id)),
    [deletableAssignments],
  )
  const selectedDeletableIds = useMemo(
    () => selectedAssignmentIds.filter((assignmentId) => deletableAssignmentIds.includes(assignmentId)),
    [deletableAssignmentIds, selectedAssignmentIds],
  )
  const referenceAssignmentId = selectedAssignmentIds[0] ?? null
  const allSelectableSelected = selectableAssignmentIds.length > 0
    && selectableAssignmentIds.every((assignmentId) => selectedAssignmentIds.includes(assignmentId))

  const toggleAssignmentSelection = (assignmentId: string, checked: boolean) => {
    setSelectedAssignmentIds((current) => {
      if (checked) {
        return current.includes(assignmentId) ? current : [...current, assignmentId]
      }
      return current.filter((id) => id !== assignmentId)
    })
  }

  const toggleSelectAll = (checked: boolean) => {
    setSelectedAssignmentIds(checked ? selectableAssignmentIds : [])
  }

  const invalidateAssignmentQueries = (workerIds: Array<string | number | null | undefined>) => {
    queryClient.invalidateQueries({ queryKey: ['assignments'] })
    queryClient.invalidateQueries({ queryKey: ['job-assignments', String(jobId)] })
    for (const workerId of workerIds) {
      if (workerId) {
        queryClient.invalidateQueries({ queryKey: ['worker-assignments', workerId] })
      }
    }
  }

  const deleteAssignmentMutation = useMutation({
    mutationFn: (assignmentId: string) => assignmentsApi.deleteAssignment(assignmentId),
    onSuccess: (_result, assignmentId) => {
      const assignment = assignments.find((row) => String(row.id) === assignmentId)
      invalidateAssignmentQueries([assignment?.worker_id])
      setSelectedAssignmentIds((current) => current.filter((id) => id !== assignmentId))
      toast.success('Assignment deleted successfully')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (assignmentIds: string[]) => {
      const results = await Promise.allSettled(
        assignmentIds.map((assignmentId) => assignmentsApi.deleteAssignment(assignmentId)),
      )
      const failed = results.filter((result) => result.status === 'rejected').length
      if (failed > 0) {
        throw new Error(`${failed} assignment${failed === 1 ? '' : 's'} could not be deleted.`)
      }
      return assignmentIds
    },
    onSuccess: (assignmentIds) => {
      const workerIds = assignmentIds.map((assignmentId) => (
        assignments.find((row) => String(row.id) === assignmentId)?.worker_id
      ))
      invalidateAssignmentQueries(workerIds)
      toast.success(`Deleted ${assignmentIds.length} assignment${assignmentIds.length === 1 ? '' : 's'}.`)
      setSelectedAssignmentIds([])
      setBulkDeleteOpen(false)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const bulkStatusMutation = useMutation({
    mutationFn: async ({
      transition,
      note,
      assignmentIds,
    }: {
      transition: JobWorkflowTransition
      note?: string
      assignmentIds: string[]
    }) => {
      const results = await Promise.allSettled(
        assignmentIds.map((assignmentId) => jobWorkflowApi.runtime.applyStatus({
          entity_type: 'assignment',
          entity_id: assignmentId,
          to_status: transition.to_status,
          note,
        })),
      )
      const failed = results.filter((result) => result.status === 'rejected').length
      if (failed > 0) {
        throw new Error(`${failed} assignment${failed === 1 ? '' : 's'} could not be updated.`)
      }
      return assignmentIds
    },
    onSuccess: (assignmentIds) => {
      const workerIds = assignmentIds.map((assignmentId) => (
        assignments.find((row) => String(row.id) === assignmentId)?.worker_id
      ))
      invalidateAssignmentQueries(workerIds)
      toast.success(`Updated status for ${assignmentIds.length} assignment${assignmentIds.length === 1 ? '' : 's'}.`)
      setBulkStatusOpen(false)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const handleBulkStatusTransition = (transition: JobWorkflowTransition, note?: string) => {
    if (selectedAssignmentIds.length === 0) {
      return
    }

    bulkStatusMutation.mutate({
      transition,
      note,
      assignmentIds: selectedAssignmentIds,
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    )
  }

  if (!assignments.length) {
    return (
      <Empty>
        <div className="text-center">
          <p className="text-lg font-medium">No assignments yet</p>
          <p className="text-sm text-muted-foreground">This job does not have any worker assignments yet.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button asChild disabled={jobModificationBlocked}>
              <Link to={withReturnTo(withServiceInstanceId(`/job/workerassignment?job_id=${encodeURIComponent(String(jobId))}`, serviceInstanceId), currentReturnPath)}>
                <Users className="mr-2 h-4 w-4" />
                Add from map
              </Link>
            </Button>
            <Button variant="outline" asChild disabled={jobModificationBlocked}>
              <Link to={withReturnTo(withServiceInstanceId(`/assignments/create?job_id=${encodeURIComponent(String(jobId))}`, serviceInstanceId), currentReturnPath)}>
                Create manually
              </Link>
            </Button>
          </div>
        </div>
      </Empty>
    )
  }

  return (
    <>
      {selectedAssignmentIds.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-border/70 bg-muted/20 px-3 py-2">
          <span className="text-sm text-foreground">
            {selectedAssignmentIds.length} selected
          </span>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={bulkDeleteMutation.isPending || selectedDeletableIds.length === 0}
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete selected
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={bulkDeleteMutation.isPending || bulkStatusMutation.isPending}
            onClick={() => setSelectedAssignmentIds([])}
          >
            Clear selection
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={jobStatusChangeDisabled || bulkDeleteMutation.isPending || bulkStatusMutation.isPending}
            onClick={() => setBulkStatusOpen(true)}
          >
            Change status
          </Button>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-border/70">
        <table className="w-full text-sm">
          <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
            <tr>
              <th className="w-10 px-3 py-3">
                <Checkbox
                  checked={allSelectableSelected}
                  disabled={selectableAssignmentIds.length === 0 || bulkDeleteMutation.isPending || bulkStatusMutation.isPending}
                  onCheckedChange={(checked) => toggleSelectAll(Boolean(checked))}
                  aria-label="Select all assignments"
                />
              </th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Assignment Ref</th>
              <th className="px-4 py-3">Worker</th>
              <th className="px-4 py-3">Workday Entry Ref</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Payscale</th>
              <th className="px-4 py-3">Est. Payout</th>
              <th className="px-4 py-3">Actual Payout</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((assignment) => {
              const assignmentId = String(assignment.id)
              const isUnassigned = !assignment.worker_id
              const workerLabel = assignment.worker_name
                || (assignment.worker_id ? `Worker #${assignment.worker_id}` : 'Unassigned')
              const roleLabel = assignment.role_label || assignment.planner_resource_label || assignment.role || '-'
              const assignmentCanDelete = canDeleteAssignment(assignment)
              const payscale = formatAssignmentPayscale(assignment)
              const estimatedPayoutDisplay = getEstimatedAssignmentPayoutDisplay(assignment)
              const actualPayoutDisplay = getActualAssignmentPayoutDisplay(assignment)
              const assignmentWorkdayRows = workDayEntries.filter((entry) => {
                if (!assignment.worker_id || String(entry.worker ?? '') !== String(assignment.worker_id)) {
                  return false
                }
                if (assignmentTableDate && entry.work_date !== assignmentTableDate) {
                  return false
                }
                const entryAssignmentId = String(
                  (entry as { assignment?: string | number | null; assignment_id?: string | number | null }).assignment
                    ?? (entry as { assignment_id?: string | number | null }).assignment_id
                    ?? '',
                )
                return !entryAssignmentId || entryAssignmentId === assignmentId
              })

              return (
                <tr key={assignment.id} className="border-t border-border/60">
                  <td className="px-3 py-3">
                    <Checkbox
                      checked={selectedAssignmentIds.includes(assignmentId)}
                      disabled={jobModificationBlocked || bulkDeleteMutation.isPending || bulkStatusMutation.isPending}
                      onCheckedChange={(checked) => toggleAssignmentSelection(assignmentId, Boolean(checked))}
                      aria-label={`Select assignment ${assignment.assignment_ref || assignmentId}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-muted-foreground">{roleLabel}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div className="space-y-1">
                      <p>{assignment.assignment_ref || '-'}</p>
                      <AssignmentDeclineChainIndicator
                        assignment={assignment}
                        serviceInstanceId={serviceInstanceId}
                        returnPath={currentReturnPath}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {isUnassigned ? (
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                        {workerLabel}
                      </span>
                    ) : (
                      <Link to={withServiceInstanceId(`/workers/${assignment.worker_id}`, serviceInstanceId)} className="text-primary hover:underline">
                        {workerLabel}
                      </Link>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {assignmentWorkdayRows.length > 0
                      ? assignmentWorkdayRows.map((entry) => entry.workday_entry_ref || '-').join(', ')
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant="outline"
                      className={getAssignmentStatusBadgeClass(assignment.status)}
                    >
                      {getAssignmentStatusLabel(assignment.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-0.5">
                      <p className="capitalize text-foreground">{payscale.label}</p>
                      {payscale.amountLabel ? (
                        <p className="text-xs text-muted-foreground">{payscale.amountLabel}</p>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">{formatAssignmentPayoutDisplay(estimatedPayoutDisplay, formatCurrency)}</td>
                  <td className="px-4 py-3">{formatAssignmentPayoutDisplay(actualPayoutDisplay, formatCurrency)}</td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Assignment actions">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64 rounded-lg">
                        <DropdownMenuItem asChild>
                          <Link
                            to={withReturnTo(
                              withServiceInstanceId(
                                `/job/workerassignment?job_id=${encodeURIComponent(String(jobId))}&assignment_id=${encodeURIComponent(assignmentId)}`,
                                serviceInstanceId,
                              ),
                              currentReturnPath,
                            )}
                          >
                            View
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!assignment.worker_id || !serviceInstanceId}
                          onSelect={(event) => {
                            event.preventDefault()
                            if (!assignment.worker_id || !serviceInstanceId) {
                              return
                            }
                            const params = new URLSearchParams()
                            params.set('service_instance_id', String(serviceInstanceId))
                            params.set('job_id', String(jobId))
                            params.set('worker_id', String(assignment.worker_id))
                            params.set('assignment_id', assignmentId)
                            params.set('return_to', currentReturnPath)
                            onNavigate(`/job/worker/draft-invoice?${params.toString()}`)
                          }}
                        >
                          Draft invoice
                        </DropdownMenuItem>
                        {assignment.worker_id ? (
                          <DropdownMenuItem asChild>
                            <Link
                              to={withReturnTo(
                                withServiceInstanceId(
                                  `/job/worker/payments?job_id=${encodeURIComponent(String(jobId))}&worker_id=${encodeURIComponent(String(assignment.worker_id))}&action=record`,
                                  serviceInstanceId,
                                ),
                                currentReturnPath,
                              )}
                            >
                              <WalletCards className="mr-2 h-4 w-4" />
                              Record payout
                            </Link>
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem disabled>
                            <WalletCards className="mr-2 h-4 w-4" />
                            Record payout
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!assignment.worker_id}
                          onSelect={(event) => {
                            event.preventDefault()
                            if (!assignment.worker_id) {
                              return
                            }
                            onOpenWorkdayEntries(assignment)
                          }}
                        >
                          Workday entries
                        </DropdownMenuItem>
                        {isTerminalAssignmentStatus(assignment.status) || jobModificationBlocked ? (
                          <DropdownMenuItem disabled>Edit</DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem asChild>
                            <Link
                              to={withReturnTo(
                                withServiceInstanceId(
                                  `/assignments/${encodeURIComponent(assignmentId)}/edit`,
                                  serviceInstanceId,
                                ),
                                currentReturnPath,
                              )}
                            >
                              Edit
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                          Change status
                        </DropdownMenuLabel>
                        <div className="px-2 pb-2">
                          <WorkflowStatusSelect
                            entityType="assignment"
                            entityId={assignment.id}
                            currentValue={assignment.workflow_status_label || getAssignmentStatusLabel(assignment.status)}
                            disabled={jobStatusChangeDisabled}
                            className="h-8 text-xs"
                            onApplied={() => {
                              queryClient.invalidateQueries({ queryKey: ['assignments'] })
                              queryClient.invalidateQueries({ queryKey: ['job-assignments', String(jobId)] })
                              if (assignment.worker_id) {
                                queryClient.invalidateQueries({ queryKey: ['worker-assignments', assignment.worker_id] })
                              }
                            }}
                          />
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          disabled={jobModificationBlocked || !assignmentCanDelete || deleteAssignmentMutation.isPending}
                          title={assignmentCanDelete ? undefined : getAssignmentDeleteBlockedMessage()}
                          onSelect={(event) => {
                            event.preventDefault()
                            if (assignmentCanDelete) {
                              deleteAssignmentMutation.mutate(assignmentId)
                            }
                          }}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              )
            })}
            <tr className="border-t border-border/60 bg-muted/20 font-medium">
              <td className="px-3 py-3" />
              <td className="px-4 py-3" colSpan={6}>Worker payout totals</td>
              <td className="px-4 py-3">{formatCurrency(estimatedWorkerPayoutTotal)}</td>
              <td className="px-4 py-3">{formatCurrency(actualPayoutTotal)}</td>
              <td className="px-4 py-3" />
            </tr>
          </tbody>
        </table>
      </div>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !bulkDeleteMutation.isPending && setBulkDeleteOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected assignments</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {selectedDeletableIds.length} selected assignment{selectedDeletableIds.length === 1 ? '' : 's'}? Assigned workers will be removed from this job. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkDeleteMutation.isPending || selectedDeletableIds.length === 0}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => bulkDeleteMutation.mutate(selectedDeletableIds)}
            >
              Delete selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkStatusOpen} onOpenChange={(open) => !bulkStatusMutation.isPending && setBulkStatusOpen(open)}>
        <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-md">
          <div className="border-b bg-muted/30 px-6 py-5">
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="text-xl">Update assignment status</DialogTitle>
              <DialogDescription>
                Choose the new status for {selectedAssignmentIds.length} selected assignment{selectedAssignmentIds.length === 1 ? '' : 's'}.
                You can add a reason or note when required.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-5">
            {referenceAssignmentId ? (
              <WorkflowStatusSelect
                entityType="assignment"
                entityId={referenceAssignmentId}
                disabled={jobStatusChangeDisabled || bulkStatusMutation.isPending}
                className="w-full rounded-xl"
                contextDescription={`Applies to ${selectedAssignmentIds.length} selected assignment${selectedAssignmentIds.length === 1 ? '' : 's'}.`}
                onSelectTransition={handleBulkStatusTransition}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default JobAssignmentsTable
